import * as RDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { type ReactElement, type ReactNode, type RefObject } from "react";

import { cn } from "../utils/cn";

export type DrawerSize = "sm" | "md" | "lg";

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: DrawerSize;
  showClose?: boolean;
  /** Where focus goes on close when the drawer wasn't opened from a Radix
   * trigger (Radix would otherwise drop it on <body>). */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

const SIZES: Record<DrawerSize, string> = {
  sm: "max-w-[360px]",
  md: "max-w-[440px]",
  lg: "max-w-[620px]",
};

/** Right-sliding side panel. Shares Radix' Dialog primitives with Modal
 * so the focus-trap, ESC-to-close and a11y semantics carry through —
 * only the position and slide animation differ. Used for the candidate
 * filter panel on the Longlist, where dropping a centered Modal would
 * obscure the list the user is filtering. */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
  showClose = true,
  returnFocusRef,
}: DrawerProps): ReactElement {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-neutral-900/50 backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=open]:fade-in",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out",
          )}
        />
        <RDialog.Content
          onCloseAutoFocus={(event) => {
            if (!returnFocusRef?.current) return;
            event.preventDefault();
            returnFocusRef.current.focus();
          }}
          className={cn(
            "fixed bottom-0 right-0 top-0 z-50 flex w-full flex-col bg-neutral-0 shadow-xl",
            "data-[state=open]:animate-in data-[state=open]:slide-in-from-right",
            "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right",
            SIZES[size],
          )}
        >
          {title && (
            <div className="flex items-center justify-between border-b border-border-default px-6 py-4">
              <RDialog.Title className="m-0 text-lg font-semibold">{title}</RDialog.Title>
              {showClose && (
                <RDialog.Close
                  aria-label="Schließen"
                  className="inline-flex rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <X size={20} />
                </RDialog.Close>
              )}
            </div>
          )}
          {description && (
            <RDialog.Description className="px-6 pt-3 text-sm text-text-secondary">
              {description}
            </RDialog.Description>
          )}
          <div
            // A scrollable region needs a tab stop so keyboard users can scroll a long
            // body (WCAG 2.1.1, axe scrollable-region-focusable).
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            role="region"
            aria-label={typeof title === "string" ? title : "Inhalt"}
            className="flex-1 overflow-auto px-6 py-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
          >
            {children}
          </div>
          {footer && (
            <div className="flex flex-wrap justify-end gap-3 border-t border-border-default px-6 py-4">
              {footer}
            </div>
          )}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
